"use client";

import React, {
  startTransition,
  useActionState,
  useEffect,
  useState,
} from "react";
import SectionLayout from "../ui/SectionLayout";
import { submitAssessmentForm } from "@/actions/assessment-form";
import ConfirmModal from "../ui/ConfirmModal";
import { ASSESSMENT_INITIAL_STATE } from "@/services/form.service";
import { SectionProps } from "./model";
import { formatPhoneNumberObject, getDefaultCmsSection } from "@/utils/helper";
import TextInput from "../ui/inputs/TextInput";
import FormDateTimeFields from "@/components/ui/inputs/FormDateTimeFields";
import TextAreaInput from "../ui/inputs/TextAreaInput";
import FormLayout from "../ui/inputs/FormLayout";
import StoreSelect from "../ui/inputs/StoreSelect";
import { useReCaptcha } from "next-recaptcha-v3";
import { ApplicationDataType } from "@/services/queries/get-application-details";

const AssessmentFormSectionComponent: React.FC<
  SectionProps<{
    app: ApplicationDataType;
    title: string;
    description: string;
    messageTextPre?: string;
    messageTextPost?: string;
  }>
> = ({ app, cmsData, cmsSection }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isInvalidCaptchaOpen, setIsInvalidCaptchaOpen] = useState(false);
  const [formState, formAction, isPending] = useActionState(
    (prevState: any, formData: FormData) => {
      return submitAssessmentForm(prevState, formData).then((newState) => {
        if (newState.isSuccess) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "assessmentform_submitted",
          });
          window?.pageVisitTracker?.track("AssessmentFormSubmit", {});
        }

        return newState;
      });
    },
    ASSESSMENT_INITIAL_STATE,
  );
  useEffect(() => {
    if (!("recaptchaIsValid" in formState)) {
      return;
    }

    if (!formState.recaptchaIsValid) {
      setIsInvalidCaptchaOpen(true);
    } else {
      setIsOpen(formState.isSuccess ?? false);
    }
  }, [formState]);
  const {
    firstName,
    lastName,
    address,
    zip,
    city,
    phone,
    email,
    message,
    time,
    date,
  } = formState?.data || {};

  // With explicit spacing a section without ui has no padding: explicitSpacingUpgrade wrote this one's (lg).
  cmsSection = {
    ...{
      ui: cmsSection.explicitSpacing
        ? {}
        : {
            padding: "lg",
            with: "xl",
          },
    },
    ...cmsSection,
  };

  const { executeRecaptcha } = useReCaptcha();
  const submit = (payload: FormData) => {
    (async () => {
      try {
        const token = await executeRecaptcha("AssessmentForm");
        payload.append("recaptchaToken", token);
      } catch (error) {
        console.log("[ReCaptcha Error]:", error);
        payload.delete("recaptchaToken");
      } finally {
        startTransition(() => {
          formAction(payload);
        });
      }
    })();
  };

  return (
    <SectionLayout cmsSection={getDefaultCmsSection(cmsSection)}>
      <ConfirmModal
        description="Får du brug for at ændre, justere eller aflyse din bestilling, kan du kontakte os på:"
        title="Tak for din bestilling"
        setOpen={(isOpen) => setIsOpen(isOpen)}
        isOpen={isOpen}
        contactInfo={{
          phone: formatPhoneNumberObject(formState.contactInfo?.phone ?? ""),
          email: formState.contactInfo?.email?.address ?? "",
        }}
      />
      <ConfirmModal
        modalType="error"
        description="Prøv venligst igen. Hvis du fortsætter med at opleve denne fejl så kontakt venligst support."
        title="Din forespørgsel blev blokeret"
        setOpen={(isOpen) => setIsInvalidCaptchaOpen(isOpen)}
        isOpen={isInvalidCaptchaOpen}
      />

      <div className="flex flex-col gap-4 flex-1 w-full homepage-text-sm">
        <div className="homepage-text-xl" id="assessment-form">
          {cmsSection.specifics.title}
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="md:w-1/3">
            {cmsSection.specifics.description && (
              <div
                className="styled-description homepage-text-sm"
                dangerouslySetInnerHTML={{
                  __html: `${cmsSection.specifics.description ?? ""}`,
                }}
              ></div>
            )}
          </div>
          <div className="flex flex-1 md:w-2/3">
            <FormLayout
              submitLabel="Bestil"
              isPending={isPending}
              featureFlags={cmsData?.featureFlags}
              formAction={submit}
            >
              <input
                type="hidden"
                name="messageTextPre"
                defaultValue={cmsSection.specifics.messageTextPre}
              />
              <input
                type="hidden"
                name="messageTextPost"
                defaultValue={cmsSection.specifics.messageTextPost}
              />
              <StoreSelect
                cmsData={cmsData}
                label="Vælg butik"
                name="resourceGroupId"
                stores={app.stores}
              />
              <FormDateTimeFields
                cmsData={cmsData}
                form="assessment"
                date={date}
                time={time}
                errors={formState?.zodErrors}
              />
              <TextInput
                cmsData={cmsData}
                name="firstName"
                label="Fornavn"
                defaultValue={firstName}
                errors={formState?.zodErrors?.firstName}
                required={cmsData?.forms.nameRequired}
              />
              <TextInput
                cmsData={cmsData}
                name="lastName"
                label="Efternavn"
                defaultValue={lastName}
                errors={formState?.zodErrors?.lastName}
                required={cmsData?.forms.nameRequired}
              />
              <TextInput
                cmsData={cmsData}
                className="col-span-6"
                name="address"
                label="Adresse"
                defaultValue={address}
                errors={formState?.zodErrors?.address}
                required={cmsData?.forms.addressRequired}
              />
              <TextInput
                cmsData={cmsData}
                className="col-span-2"
                name="zip"
                label="Postnr"
                defaultValue={zip}
                errors={formState?.zodErrors?.zip}
                required={cmsData?.forms.zipRequired}
              />
              <TextInput
                cmsData={cmsData}
                className="col-span-4"
                name="city"
                label="By"
                defaultValue={city}
                errors={formState?.zodErrors?.city}
                required={cmsData?.forms.cityRequired}
              />
              <TextInput
                cmsData={cmsData}
                className="col-span-3"
                name="phone"
                label="Telefonnummer"
                defaultValue={phone}
                errors={formState?.zodErrors?.phone}
                required={cmsData?.forms.phoneRequired}
              />
              <TextInput
                cmsData={cmsData}
                className="col-span-3"
                name="email"
                label="E-mail"
                defaultValue={email}
                errors={formState?.zodErrors?.email}
                required={cmsData?.forms.emailRequired}
              />
              <TextAreaInput
                cmsData={cmsData}
                name="message"
                label="Besked"
                defaultValue={message}
                errors={formState?.zodErrors?.message}
                required={cmsData?.forms.messageRequired}
              />
            </FormLayout>
          </div>
        </div>
      </div>
    </SectionLayout>
  );
};

export default AssessmentFormSectionComponent;
